import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { hasZyVersionNodes } from '@/lib/zy-tiptap-utils';
import { useEditorState, type Editor } from '@tiptap/react';
import { SquareSplitHorizontal } from 'lucide-react';
import { Button } from '../../tiptap-ui-primitive/button';

export interface ZyVersionSplitViewButtonProps {
    isSplitView: boolean;
    onToggleSplitView: () => void;
    editor?: Editor | null;
}

export function ZyVersionSplitViewButton({ isSplitView, onToggleSplitView }: ZyVersionSplitViewButtonProps) {
    const { editor } = useTiptapEditor();

    const hasVersions =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? hasZyVersionNodes(ed) : false),
        }) ?? false;

    const buildTooltip = () => {
        if (!isSplitView && !hasVersions) {
            return 'No versions to show in split view';
        }
        return isSplitView ? 'Exit split view' : 'Enter split view';
    };

    const isZyVersionInSchema = isNodeInSchema('zyVersionBlock', editor) && isNodeInSchema('zyVersionInline', editor);
    if (!isZyVersionInSchema) {
        return null;
    }

    return (
        <Button
            type="button"
            disabled={!isSplitView && !hasVersions}
            data-disabled={!isSplitView && !hasVersions}
            data-style="ghost"
            data-active-state={isSplitView ? 'on' : 'off'}
            aria-pressed={isSplitView}
            onClick={onToggleSplitView}
            aria-label={buildTooltip()}
            tooltip={buildTooltip()}
        >
            <SquareSplitHorizontal
                className="tiptap-button-icon size-4"
                aria-hidden
            />
        </Button>
    );
}

export default ZyVersionSplitViewButton;
